import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TodayIcon from "@mui/icons-material/Today";
import {
  Box,
  Button,
  ButtonGroup,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { DateTime } from "luxon";
import * as styles from "./styles";

export interface CalendarProps {
  loading?: boolean;
  title?: string | JSX.Element;
  data: {
    day: string; // yyyy-MM-dd
    color?: string;
    hoverColor?: string;
    backgroundColor?: string;
    hoverBackgroundColor?: string;
    content?: JSX.Element;
    onClick?: (day: string) => void;
  }[];
  month: DateTime;
  setMonth: (m: DateTime) => void;
  noPast?: boolean;
  monthFormat?: string;
  showTodayButton?: boolean;
}

export default function Calendar({
  loading,
  title,
  data,
  month,
  setMonth,
  noPast,
  monthFormat,
  showTodayButton,
}: CalendarProps) {
  const dayCells = getDayCells();

  return (
    <div data-testid="calendarComponent">
      <Toolbar />
      <TableContainer sx={styles.tableContainer}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.dotwHeader}>Mo</TableCell>
              <TableCell sx={styles.dotwHeader}>Tu</TableCell>
              <TableCell sx={styles.dotwHeader}>We</TableCell>
              <TableCell sx={styles.dotwHeader}>Th</TableCell>
              <TableCell sx={styles.dotwHeader}>Fr</TableCell>
              <TableCell sx={styles.dotwHeader}>Sa</TableCell>
              <TableCell sx={styles.dotwHeader}>Su</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingBar />
            ) : (
              dayCells.map((g, i) => (
                <TableRow key={i}>{g.map((c) => c)}</TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  function Toolbar() {
    return (
      <Stack
        style={{ marginBottom: "10px" }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <span style={{ fontWeight: "bold", fontSize: "18px" }}>
          {title && title}
        </span>
        <MonthControls />
      </Stack>
    );
  }

  function MonthControls() {
    const currentMonth =
      month.toFormat("yyyy-MM") === DateTime.now().toFormat("yyyy-MM");

    return (
      <Stack direction="row" spacing={3} alignItems="center">
        <Box sx={{ fontSize: "23px" }}>
          {month.toFormat(monthFormat || "yyyy-MM")}
        </Box>
        {showTodayButton && !currentMonth && (
          <Tooltip title="Go to current month">
            <Button
              sx={{
                "&:hover": {
                  border: "1px solid #000",
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
                height: "36px",
                backgroundColor: "#fff",
                color: "#000",
                border: "1px solid #000",
              }}
              onClick={() => setMonth(DateTime.now().set({ day: 1 }))}
            >
              <TodayIcon />
            </Button>
          </Tooltip>
        )}
        <ButtonGroup sx={{ backgroundColor: "#fff" }}>
          <Button
            sx={{
              "&:hover": { border: "1px solid #000" },
              color: "#000",
              border: "1px solid #000",
            }}
            disabled={noPast && currentMonth}
            onClick={() => setMonth(month.minus({ month: 1 }))}
          >
            <ArrowBackIosNewIcon />
          </Button>
          <Button
            sx={{
              "&:hover": { border: "1px solid #000" },
              color: "#000",
              border: "1px solid #000",
            }}
            onClick={() => setMonth(month.plus({ month: 1 }))}
          >
            <ArrowForwardIosIcon />
          </Button>
        </ButtonGroup>
      </Stack>
    );
  }

  function LoadingBar() {
    return (
      <TableRow>
        <TableCell colSpan={7}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#009688", // bar color
              },
              backgroundColor: "#eee", // track color
            }}
          />
        </TableCell>
      </TableRow>
    );
  }

  function getDayCells() {
    const numberOfDays = month.daysInMonth;
    const dayCells = [...Array(numberOfDays)].map((_, i) => (
      <DayCell key={i} index={i} />
    ));

    // Add some empty cells for correct start
    const dayNumber = parseInt(month.toFormat("c"));
    for (let i = 1; i < dayNumber; i++) {
      dayCells.unshift(<TableCell key={`0${i}`}></TableCell>);
    }

    return new Array(Math.ceil(dayCells.length / 7))
      .fill("")
      .map((_, i) => dayCells.slice(i * 7, (i + 1) * 7));
  }

  function DayCell(props: { index: number }) {
    const day = month.set({ day: props.index + 1 });
    const isPast = day.startOf("day") < DateTime.now().startOf("day");
    const dayData = data.find((d) => d.day === day.toFormat("yyyy-MM-dd"));

    return (
      <TableCell
        key={props.index}
        style={{
          backgroundColor: isPast
            ? "#eee"
            : dayData?.backgroundColor
              ? dayData.backgroundColor
              : undefined,
          verticalAlign: "top",
          cursor: dayData?.onClick ? "pointer" : undefined,
        }}
        sx={{
          ...styles.dayCell,
          ":hover": {
            color: dayData?.onClick
              ? dayData?.hoverColor || undefined
              : undefined,
            backgroundColor: dayData?.onClick
              ? dayData.hoverBackgroundColor || "rgba(255, 0, 0, 0.05)"
              : undefined,
          },
        }}
        onClick={
          dayData?.onClick
            ? () => dayData.onClick!(day.toFormat("yyyy-MM-dd"))
            : undefined
        }
      >
        <Box
          component="span"
          key={0}
          sx={{ ...styles.dayNumber, color: dayData?.color || undefined }}
        >
          {props.index + 1}
        </Box>
        {dayData?.content && (
          <div key={1} style={{ marginTop: "5px" }}>
            {dayData?.content}
          </div>
        )}
      </TableCell>
    );
  }
}
